// Reuse a short-lived product-page response; each modal still gets fresh DOM and unique IDs.
(() => {
  if (window.nsQuickViewRequests) return;
  const cache = new Map();
  let active = 0;
  const get = url => {
    const existing = cache.get(url);
    if (existing && existing.expires > Date.now()) return existing.promise;
    const entry = {expires: Date.now() + 60000};
    active++;
    entry.promise = fetch(url, {credentials: 'same-origin'}).then(async response => {
      if (!response.ok) throw new Error('Product request failed');
      const html = await response.text();
      entry.expires = Date.now() + 60000;
      return html;
    }).catch(error => {
      if (cache.get(url) === entry) cache.delete(url);
      throw error;
    }).finally(() => {active--;});
    cache.set(url, entry);
    while (cache.size > 12) cache.delete(cache.keys().next().value);
    return entry.promise;
  };
  const warm = opener => {
    const url = opener?.getAttribute('data-product-url');
    if (url && active < 2) void get(url).catch(() => {});
  };
  window.nsQuickViewRequests = {get};
  const timers = new WeakMap();
  document.addEventListener('pointerover', event => {
    if (event.pointerType === 'touch') return;
    const card = event.target.closest?.('.ns-product-card');
    if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
    clearTimeout(timers.get(card));
    timers.set(card, setTimeout(() => warm(card.querySelector('.card__link[data-product-url]')), 120));
  }, {passive: true});
  document.addEventListener('pointerout', event => {
    const card = event.target.closest?.('.ns-product-card');
    if (card && (!event.relatedTarget || !card.contains(event.relatedTarget))) clearTimeout(timers.get(card));
  }, {passive: true});
  ['focusin', 'pointerdown'].forEach(name => document.addEventListener(name, event => {
    warm(event.target.closest?.('.ns-card-actions button[data-product-url]'));
  }, {passive: true}));
})();

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.sliders = [];
      this.requestSequence = 0;
    }
    hide(preventFocus = false) {
      this.requestSequence++;
      this.sliders.forEach(slider => slider && !slider.destroyed && slider.destroy(true, true));
      this.sliders = [];
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';
      if (preventFocus) this.openedBy = null;
      super.hide();
    }
    async show(opener) {
      if (opener.getAttribute('aria-disabled') === 'true') return;
      const sequence = ++this.requestSequence;
      opener.setAttribute('aria-disabled', 'true');
      opener.setAttribute('aria-busy', 'true');
      const premium = this.classList.contains('ns-quick-view');
      if (premium) {
        this.renderPending(opener);
        super.show(opener);
      }
      try {
        const html = await window.nsQuickViewRequests.get(opener.getAttribute('data-product-url'));
        const documentHTML = new DOMParser().parseFromString(html, 'text/html');
        if (sequence !== this.requestSequence) return;
        this.productElement = documentHTML.querySelector('section[id^="MainProduct-"]');
        if (!this.productElement) throw new Error('Product section not found');
        this.preventDuplicatedIDs();
        this.removeDOMElements();
        this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
        if (this.classList.contains('ns-quick-view')) this.buildPremiumLayout();
        this.removeGalleryListSemantic();
        this.updateImageSizes();
        this.preventVariantURLSwitching();
        if (!premium) super.show(opener);
        else if (typeof trapFocus === 'function') trapFocus(this, this.querySelector('.quick-add-modal__toggle') || this.modalContent);
        if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        if (window.ProductModel) window.ProductModel.loadShopifyXR();
        this.initializeGallery();
      } catch (error) {
        if (sequence === this.requestSequence) window.location.assign(opener.getAttribute('data-product-url'));
      } finally {
        opener.removeAttribute('aria-disabled');
        opener.removeAttribute('aria-busy');
        this.modalContent.removeAttribute('aria-busy');
      }
    }
    renderPending(opener) {
      const pending = document.createElement('div');
      pending.className = 'ns-qv-loading';
      const original = opener.closest('.ns-product-card')?.querySelector('.media img');
      if (original) {
        const image = document.createElement('img');
        image.src = original.currentSrc || original.src;
        image.alt = original.alt || '';
        pending.append(image);
      }
      const copy = document.createElement('p');
      copy.textContent = 'Loading product…';
      copy.setAttribute('role', 'status');
      pending.append(copy);
      this.modalContent.replaceChildren(pending);
      this.modalContent.setAttribute('aria-busy', 'true');
    }
    setInnerHTML(element, html) {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach(oldScript => {
        const script = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attribute => script.setAttribute(attribute.name, attribute.value));
        script.textContent = oldScript.textContent;
        oldScript.replaceWith(script);
      });
    }
    removeDOMElements() {
      this.productElement.querySelectorAll('pickup-availability, product-modal, .customer').forEach(node => node.remove());
    }
    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      if (!sectionId) return;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${this.id}-${sectionId}`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach(node => {
        node.dataset.originalSection = sectionId;
      });
    }
    preventVariantURLSwitching() {
      this.modalContent.querySelectorAll('variant-radios, variant-selects').forEach(node => node.setAttribute('data-update-url', 'false'));
    }
    removeGalleryListSemantic() {
      const gallery = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!gallery) return;
      gallery.setAttribute('role', 'presentation');
      gallery.querySelectorAll('[id^="Slide-"]').forEach(node => node.setAttribute('role', 'presentation'));
    }
    updateImageSizes() {
      this.modalContent.querySelectorAll('.product__media img').forEach(img => {
        img.setAttribute('sizes', '(min-width: 750px) 550px, calc(100vw - 40px)');
        img.loading = 'eager';
      });
    }
    buildPremiumLayout() {
      const info = this.modalContent.querySelector('.product__info-container');
      if (!info) return;
      // Keep one category heading even when the theme vendor styles load later.
      info.querySelectorAll('.product__text, .ns-qv-eyebrow').forEach(node => node.remove());
      const template = this.modalContent.querySelector('.ns-qv-copy');
      if (template) {
        const copy = template.content.cloneNode(true);
        const eyebrow = copy.querySelector('.ns-qv-eyebrow');
        const description = copy.querySelector('.ns-qv-description');
        if (eyebrow) info.prepend(eyebrow);
        const title = info.querySelector('.product__title__wrapper');
        if (title && description) title.after(description);
      }
      const footer = document.createElement('div');
      footer.className = 'ns-qv-footer';
      const price = info.querySelector('.price-wrapper');
      const productForm = info.querySelector('product-form');
      const details = info.querySelector('.full-details');
      if (price) footer.append(price);
      if (productForm) footer.append(productForm);
      if (details) footer.append(details);
      info.append(footer);
      const productOuter = this.modalContent.querySelector('.product__outer');
      if (productOuter) productOuter.classList.remove('product__outer--desktop-order', 'product__outer--mobile-order', 'product__outer--thumbnail-order');
    }
    initializeGallery() {
      if (!window.Swiper) return;
      const gallery = this.modalContent.querySelector('.js-media-list');
      if (!gallery) return;
      const media = this.modalContent.querySelector('.product__media-wrapper');
      if (gallery.swiper) gallery.swiper.destroy(true, true);
      const thumbnailElement = media && media.querySelector('.js-media-sublist');
      let thumbnails;
      if (this.classList.contains('ns-quick-view') && thumbnailElement) {
        if (thumbnailElement.swiper) thumbnailElement.swiper.destroy(true, true);
        thumbnails = new Swiper(thumbnailElement, {slidesPerView: 'auto', spaceBetween: 8, watchSlidesProgress: true});
        this.sliders.push(thumbnails);
      }
      const options = {
        slidesPerView: 1,
        observer: true,
        observeParents: true,
        navigation: {nextEl: media && media.querySelector('.swiper-btn--next'), prevEl: media && media.querySelector('.swiper-btn--prev')}
      };
      if (thumbnails) options.thumbs = {swiper: thumbnails};
      const slider = new Swiper(gallery, options);
      this.sliders.push(slider);
      // Thumbnail navigation also works when the theme Swiper bundle omits Thumbs.
      if (thumbnails) thumbnailElement.querySelectorAll('.swiper-slide').forEach((slide, index) => {
        slide.setAttribute('tabindex', '0');
        slide.setAttribute('role', 'button');
        slide.setAttribute('aria-label', `Image ${index + 1}`);
        slide.addEventListener('click', () => slider.slideTo(index));
        slide.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {event.preventDefault(); slider.slideTo(index);}
        });
      });
    }
  });
}
